'use client';

import { useRef, type ReactNode } from 'react';
import Image from 'next/image';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TbRosetteDiscountCheck } from 'react-icons/tb';
import GlareHover from '@/app/components/GlareHover';
import ScrollReveal from '@/app/components/animations/ScrollReveal';
import type { CompanyProfileContent } from '@/app/lib/profile-content';

type DifferencePoint = CompanyProfileContent['difference']['points'][number];

// Cycled per point instead of a flat red on every icon, so the connector row
// reads as a spread of brand colors rather than a wall of red.
const pointAccents = ['text-brand-cta', 'text-brand-purple', 'text-brand-bg', 'text-brand-text'];

interface DifferenceSystemProps {
    eyebrow: string;
    title: string;
    body: string;
    points: DifferencePoint[];
    icons: ReactNode[];
    image: string;
    isAr: boolean;
}

function ConnectorRow({ points, icons, isAr }: { points: DifferencePoint[]; icons: ReactNode[]; isAr: boolean }) {
    const rowRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: rowRef,
        offset: ['start 0.85', 'end 0.45'],
    });
    const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

    return (
        <div ref={rowRef} className="relative">
            <div className="pointer-events-none absolute inset-x-7 top-7 hidden h-px bg-brand-bg/10 md:block" />
            <motion.div
                style={{ scaleX: lineScale }}
                className={`pointer-events-none absolute inset-x-7 top-7 hidden h-[2px] bg-gradient-to-r from-brand-purple via-brand-text to-brand-cta md:block ${
                    isAr ? 'origin-right' : 'origin-left'
                }`}
            />

            <div className="pointer-events-none absolute inset-y-0 start-7 block w-px bg-brand-bg/10 md:hidden" />
            <motion.div
                style={{ scaleY: lineScale }}
                className="pointer-events-none absolute inset-y-0 start-7 block w-[2px] origin-top bg-gradient-to-b from-brand-purple via-brand-text to-brand-cta md:hidden"
            />

            <div className="grid gap-10 md:grid-cols-4 md:gap-6">
                {points.map((point, index) => {
                    const accent = pointAccents[index % pointAccents.length];
                    return (
                        <ScrollReveal key={point.title} mode="fade-up" delay={index * 0.1}>
                            <div className="relative flex gap-5 md:block">
                                <span className={`relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-brand-bg bg-white shadow-[0_8px_24px_rgba(8,24,33,0.12)] md:mb-6 ${accent}`}>
                                    {icons[index] ?? <TbRosetteDiscountCheck className="h-6 w-6" aria-hidden="true" />}
                                </span>
                                <div>
                                    <span className={`mb-2 block font-heading text-xs font-black uppercase tracking-[0.2em] ${accent}`}>
                                        0{index + 1}
                                    </span>
                                    <h3 className="font-heading text-xl font-bold text-brand-bg">{point.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#081821]/70 md:text-base">{point.body}</p>
                                </div>
                            </div>
                        </ScrollReveal>
                    );
                })}
            </div>
        </div>
    );
}

export default function DifferenceSystem({ eyebrow, title, body, points, icons, image, isAr }: DifferenceSystemProps) {
    return (
        <section className="perf-deferred relative overflow-hidden bg-[#FBFBFB] px-5 py-24 text-[#081821] md:px-10 md:py-32">
            <div className="pointer-events-none absolute -top-24 end-[-10%] h-[420px] w-[420px] rounded-full " />

            <div className="relative mx-auto max-w-7xl">
                <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-center">
                    <ScrollReveal mode="fade-up">
                        <p className="mb-4 inline-flex items-center gap-2 font-heading text-lg font-bold text-brand-purple">
                            <TbRosetteDiscountCheck className="h-5 w-5" aria-hidden="true" />
                            {eyebrow}
                        </p>
                        <h2 className="max-w-3xl font-heading text-4xl font-black leading-tight text-brand-bg md:text-6xl">
                            {title}
                        </h2>
                        <p className="mt-6 max-w-2xl text-xl leading-relaxed text-[#081821]/75">{body}</p>
                    </ScrollReveal>

                    <ScrollReveal mode="fade-left" delay={0.16}>
                        <div className="relative mx-auto w-full max-w-[220px]">
                            <div className="nebras-floating-carpet-shadow pointer-events-none absolute -bottom-3 left-1/2 h-8 w-[70%] -translate-x-1/2 rounded-full" />
                            <div className="nebras-floating-carpet relative aspect-[3/4] overflow-hidden rounded-[2rem] shadow-[0_28px_60px_rgba(8,24,33,0.28)]">
                                <Image
                                    src={image}
                                    alt={title}
                                    fill
                                    sizes="220px"
                                    className="nebras-floating-carpet-image object-cover"
                                />
                                <GlareHover
                                    width="100%"
                                    height="100%"
                                    background="transparent"
                                    borderColor="transparent"
                                    borderRadius="0px"
                                    glareColor="#ffb53e"
                                    glareOpacity={0.25}
                                    glareAngle={-35}
                                    glareSize={220}
                                    transitionDuration={900}
                                    className="!border-0"
                                    style={{ position: 'absolute', inset: 0 }}
                                />
                            </div>
                        </div>
                    </ScrollReveal>
                </div>

                <div className="mt-16 md:mt-24">
                    <ConnectorRow points={points} icons={icons} isAr={isAr} />
                </div>
            </div>
        </section>
    );
}
